import { COMPANIES } from 'constants/Entities';
import { getCrudDataFromBackendForm } from 'utils/copilotRegistry/crudForm';
import { getLiteral } from 'utils/getLiteral';

const pickSingularPlural = (count, singularKey, pluralKey) =>
    Number(count) === 1 ? singularKey : pluralKey;

/**
 * @typedef {{ bold?: string, text?: string }} ReviewCopySegment
 */

/**
 * Review-step title and stats as plain text segments (numbers rendered bold in UI).
 * @returns {{ titleSegments: ReviewCopySegment[], statsSegments: ReviewCopySegment[] }}
 */
export const buildReviewSummarySegments = ({
    readyCount,
    createdCount,
    failedCount,
    creditsUsed,
    balance,
}) => {
    const balanceText = String(balance);

    if (readyCount === 0 && createdCount === 0 && failedCount > 0) {
        return {
            titleSegments: [
                { bold: String(failedCount) },
                {
                    text: getLiteral(
                        pickSingularPlural(
                            failedCount,
                            'title_leads_review_all_failed_suffix_singular',
                            'title_leads_review_all_failed_suffix_plural',
                        ),
                    ),
                },
            ],
            statsSegments: [
                { bold: String(failedCount) },
                { text: getLiteral('label_leads_review_stats_all_failed_middle') },
                { bold: balanceText },
            ],
        };
    }

    if (readyCount === 0 && createdCount > 0 && failedCount === 0) {
        return {
            titleSegments: [{ text: getLiteral('title_leads_review_all_reviewed') }],
            statsSegments: [
                { bold: String(creditsUsed) },
                { text: getLiteral('label_leads_review_stats_all_reviewed_middle') },
                { bold: balanceText },
            ],
        };
    }

    if (readyCount === 0 && createdCount > 0 && failedCount > 0) {
        return {
            titleSegments: [
                { text: getLiteral('title_leads_review_all_reviewed_with_failures_prefix') },
                { bold: String(failedCount) },
                { text: getLiteral('title_leads_review_all_reviewed_with_failures_suffix') },
            ],
            statsSegments: [
                { bold: String(createdCount) },
                {
                    text: getLiteral(
                        'label_leads_review_stats_all_reviewed_with_failures_after_created',
                    ),
                },
                { bold: String(failedCount) },
                {
                    text: getLiteral(
                        'label_leads_review_stats_all_reviewed_with_failures_after_failed',
                    ),
                },
                { bold: String(creditsUsed) },
                {
                    text: getLiteral(
                        'label_leads_review_stats_all_reviewed_with_failures_after_credits',
                    ),
                },
                { bold: balanceText },
            ],
        };
    }

    if (failedCount > 0) {
        return {
            titleSegments: [
                { bold: String(readyCount) },
                {
                    text: getLiteral(
                        pickSingularPlural(
                            readyCount,
                            'title_leads_review_with_failures_ready_suffix_singular',
                            'title_leads_review_with_failures_ready_suffix_plural',
                        ),
                    ),
                },
                { bold: String(failedCount) },
                {
                    text: getLiteral(
                        pickSingularPlural(
                            failedCount,
                            'title_leads_review_with_failures_failed_suffix_singular',
                            'title_leads_review_with_failures_failed_suffix_plural',
                        ),
                    ),
                },
            ],
            statsSegments: [
                { bold: String(readyCount) },
                { text: getLiteral('label_leads_review_stats_with_failures_after_ready') },
                { bold: String(failedCount) },
                { text: getLiteral('label_leads_review_stats_with_failures_after_failed') },
                { bold: String(creditsUsed) },
                { text: getLiteral('label_leads_review_stats_with_failures_after_credits') },
                { bold: balanceText },
            ],
        };
    }

    return {
        titleSegments: [
            { bold: String(readyCount) },
            {
                text: getLiteral(
                    pickSingularPlural(
                        readyCount,
                        'title_leads_review_ready_suffix_singular',
                        'title_leads_review_ready_suffix_plural',
                    ),
                ),
            },
        ],
        statsSegments: [
            { bold: String(readyCount) },
            {
                text: getLiteral(
                    pickSingularPlural(
                        readyCount,
                        'label_leads_review_stats_no_failures_lead_suffix_singular',
                        'label_leads_review_stats_no_failures_lead_suffix_plural',
                    ),
                ),
            },
            { bold: String(creditsUsed) },
            { text: getLiteral('label_leads_review_stats_no_failures_after_credits') },
            { bold: balanceText },
        ],
    };
};

export const buildPreviewRequest = ({ category, radiusKm, origin }) => ({
    lat: String(origin.lat),
    lng: String(origin.lng),
    radiusMeters: String(Math.round(Number(radiusKm) * 1000)),
    query: category || '',
});

export const buildPreviewPaginationRequest = ({ pageQueryId }) => ({
    pageQueryId: String(pageQueryId),
});

export const mergePreviewLeads = (existing = [], incoming = []) => {
    const seen = new Set(existing.map((lead) => String(lead.id)));
    const merged = [...existing];
    incoming.forEach((lead) => {
        const key = String(lead.id);
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(lead);
    });
    return merged;
};

export const getActiveTabLeadIds = (activeLeads = []) => activeLeads.map((lead) => lead.id);

export const areAllActiveTabLeadsSelected = (selectedIds = [], activeLeads = []) => {
    if (!activeLeads.length) return false;
    const ids = new Set(selectedIds.map((id) => String(id)));
    return activeLeads.every((lead) => ids.has(String(lead.id)));
};

export const deselectAllActiveTabLeads = (selectedIds = [], activeLeads = []) => {
    const activeIds = new Set(activeLeads.map((lead) => String(lead.id)));
    return selectedIds.filter((id) => !activeIds.has(String(id)));
};

export const selectAllActiveTabLeads = (selectedIds = [], activeLeads = []) => {
    const next = new Set(selectedIds);
    activeLeads.forEach((lead) => next.add(lead.id));
    return Array.from(next);
};

export const toggleSelectAllActiveTabLeads = (selectedIds = [], activeLeads = []) =>
    areAllActiveTabLeadsSelected(selectedIds, activeLeads)
        ? deselectAllActiveTabLeads(selectedIds, activeLeads)
        : selectAllActiveTabLeads(selectedIds, activeLeads);

export const splitByBucket = (leads = []) => ({
    new: leads.filter((lead) => !lead.potential_duplicate),
    duplicates: leads.filter((lead) => lead.potential_duplicate),
});

export const getSelectedLeads = (leads = [], selectedIds = []) => {
    const ids = new Set(selectedIds);
    return leads.filter((lead) => ids.has(lead.id));
};

export const toAccountCrudData = (row) => getCrudDataFromBackendForm(COMPANIES.trueName, row?.form);

export const isSameReviewRowId = (row, rowId) => {
    if (rowId == null || rowId === '') return false;
    const target = String(rowId);
    return String(row?.id) === target || String(row?.sourceLead?.id) === target;
};

export const markReviewRowAsCreated = (rows, rowId, creationDate) =>
    rows.map((row) =>
        isSameReviewRowId(row, rowId) ? { ...row, status: 'created', creationDate } : row,
    );
